import React from 'react'
import { Box } from '@mui/material'
import { PSignificance } from '../../../types/carbon/models/PSignificance'
import { SettingsSwitch, SettingsSelect, SettingsNumberInput, SettingsTextField, SettingsRow } from './SettingsControls'

interface SignificanceSectionProps {
  significance: PSignificance
  onChange: (significance: PSignificance) => void
}

const sigTypeOptions = [
  { value: 'ColumnGroups', label: 'Column Groups' },
  { value: 'RowGroups', label: 'Row Groups' },
]

const sigStatOptions = [
  { value: 'Z', label: 'Z-test' },
  { value: 'T', label: 'T-test' },
]

const headcountOptions = [
  { value: 'Weighted', label: 'Weighted' },
  { value: 'Unweighted', label: 'Unweighted' },
  { value: 'EffectiveBase', label: 'Effective Base' },
]

const SignificanceSection: React.FC<SignificanceSectionProps> = ({ significance, onChange }) => {
  const update = (patch: Partial<PSignificance>) => {
    // Auto-enable visible when changing any sub-setting (implies user wants significance active)
    const autoVisible = !('visible' in patch) && !significance.visible
      ? { visible: true }
      : {}
    onChange({ ...significance, ...autoVisible, ...patch })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <SettingsSwitch label="Visible" checked={significance.visible} onChange={v => update({ visible: v })} />

      <SettingsSelect label="Type" value={significance.type || 'ColumnGroups'} options={sigTypeOptions} onChange={v => update({ type: v as PSignificance['type'] })} />

      <SettingsRow>
        <SettingsSelect label="Prop Stat" value={significance.propStat} options={sigStatOptions} onChange={v => update({ propStat: v as PSignificance['propStat'] })} />
        <SettingsSelect label="Mean Stat" value={significance.meanStat} options={sigStatOptions} onChange={v => update({ meanStat: v as PSignificance['meanStat'] })} />
      </SettingsRow>

      <SettingsRow>
        <SettingsSwitch label="Auto Param" checked={significance.autoParam} onChange={v => update({ autoParam: v })} />
        {!significance.autoParam && (
          <SettingsTextField label="Stats Param" value={significance.statsParam || ''} onChange={v => update({ statsParam: v })} />
        )}
      </SettingsRow>

      <SettingsRow>
        <SettingsSwitch label="Append Letters" checked={significance.appendLetters} onChange={v => update({ appendLetters: v })} />
      </SettingsRow>

      <SettingsSelect label="Headcount" value={significance.headcount} options={headcountOptions} onChange={v => update({ headcount: v as PSignificance['headcount'] })} />

      <SettingsRow>
        <SettingsNumberInput label="Level 1" value={significance.sigLevel1 ?? 95} onChange={v => update({ sigLevel1: v })} min={0} max={100} />
        <SettingsNumberInput label="Level 2" value={significance.sigLevel2 ?? 99} onChange={v => update({ sigLevel2: v })} min={0} max={100} />
      </SettingsRow>

      <SettingsRow>
        <SettingsSwitch label="One-Tailed" checked={significance.oneTailed} onChange={v => update({ oneTailed: v })} />
        <SettingsSwitch label="Use level 1 only (52 letter tests)" checked={significance.lettersLong} onChange={v => update({ lettersLong: v })} />
      </SettingsRow>
      <SettingsRow>
        <SettingsSwitch label="Skip Base < 30" checked={significance.skipBase30} onChange={v => update({ skipBase30: v })} />
        <SettingsSwitch label="Skip Cell < 5" checked={significance.skipCell5} onChange={v => update({ skipCell5: v })} />
      </SettingsRow>
    </Box>
  )
}

export default SignificanceSection
